import React, { useState, useRef, useEffect } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Modal from "../../components/Modal";
import Pagination from "../../components/Pagination";
import { useUploadExcelSheet } from "./uploadExcelSheetData";
import "./index.scss";

// ── Icon helpers ──────────────────────────────────────────────────────────────
const IconEye = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const IconFolder = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </svg>
);
const IconResend = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);
const IconFullscreen = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 3 21 3 21 9" />
    <polyline points="9 21 3 21 3 15" />
    <line x1="21" y1="3" x2="14" y2="10" />
    <line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

// ── 3-dot action menu ─────────────────────────────────────────────────────────
const RowActionMenu = ({ row, onResend, onView, onViewDocuments }) => {
  const [open, setOpen] = useState(false);
  const [up, setUp] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const toggle = (e) => {
    e.stopPropagation();
    if (!open && ref.current)
      setUp(
        window.innerHeight - ref.current.getBoundingClientRect().bottom < 150,
      );
    setOpen((p) => !p);
  };

  return (
    <div className="ues-action-menu" ref={ref}>
      <button className="ues-action-menu__trigger" onClick={toggle}>
        ⋮
      </button>
      {open && (
        <div
          className={`ues-action-menu__dropdown${up ? " ues-action-menu__dropdown--up" : ""}`}
        >
          <button
            className="ues-action-menu__item"
            onClick={() => {
              setOpen(false);
              onView(row);
            }}
          >
            {/* <IconEye /> */}
            View All Data
          </button>
          <button
            className="ues-action-menu__item"
            onClick={() => {
              setOpen(false);
              onViewDocuments(row);
            }}
          >
            {/* <IconFolder /> */}
            View Documents
          </button>
          <button
            className="ues-action-menu__item"
            // onClick={() => {
            //   setOpen(false);
            //   onResend(row);
            // }}
          >
            {/* <IconResend /> */}
            Resend KYC Link
          </button>
        </div>
      )}
    </div>
  );
};

// ── View All Data modal ────────────────────────────────────────────────────────
const ViewModal = ({ isOpen, onClose, row }) => {
  if (!row) return null;
  const multiData = Array.isArray(row.data) ? row.data : [];
  const singleFields = [
    { label: "PAN", value: row.pan },
    { label: "MSME", value: row.msme },
    { label: "Bank Beneficiary Name", value: row.bankBenificiaryName },
    { label: "A/C No.", value: row.acNo },
    { label: "IFSC", value: row.ifsc },
    { label: "Bank", value: row.bank },
    { label: "Created By", value: row.createdBy },
    { label: "Created On", value: row.createdOn },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Entity Details"
      size="large"
      entityID={row.entityId}
    >
      <div className="ues-view-modal">
        <div className="ues-view-modal__hero">
          <div className="ues-view-modal__avatar">
            {(row.companyName || "?")[0].toUpperCase()}
          </div>
          <div className="ues-view-modal__hero-info">
            <p className="ues-view-modal__company">{row.companyName || "—"}</p>
            <p className="ues-view-modal__entityid">{row.entityId}</p>
          </div>
          <span
            className={`ues-view-modal__badge${row.bankBenificiaryName?.toLowerCase() === "yes" ? " ues-view-modal__badge--yes" : ""}`}
          >
            MSME: {row.msme || "—"}
          </span>
        </div>
        <div className="ues-view-modal__divider" />
        <div className="ues-view-modal__grid">
          {singleFields.map((f) => (
            <div key={f.label} className="ues-view-modal__field">
              <span className="ues-view-modal__field-label">{f.label}</span>
              <span className="ues-view-modal__field-value">
                {f.value || "—"}
              </span>
            </div>
          ))}
        </div>
        {multiData.length > 0 && (
          <>
            <div className="ues-view-modal__divider" />
            <div className="ues-view-modal__multi-table">
              <table className="ues-multi-detail-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Ledger Name</th>
                    <th>State</th>
                    <th>GSTIN / UIN</th>
                  </tr>
                </thead>
                <tbody>
                  {multiData.map((item, idx) => (
                    <tr key={item.id || idx}>
                      <td className="ues-td--num">{idx + 1}</td>
                      <td>{item.nameOfLedger || "—"}</td>
                      <td>{item.stateName || "—"}</td>
                      <td>{item.gstiN_UIN || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        <div className="ues-view-modal__footer">
          <button className="ues-btn ues-btn--cancel" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ── Document card ─────────────────────────────────────────────────────────────
const DocCard = ({ doc, blobInfo }) => {
  const isImage =
    doc.fileType &&
    [".png", ".jpg", ".jpeg", ".gif", ".webp", ".bmp"].includes(
      doc.fileType.toLowerCase(),
    );
  const isPdf = doc.fileType && doc.fileType.toLowerCase() === ".pdf";

  const openFullscreen = () => {
    if (blobInfo?.url) {
      window.open(blobInfo.url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="ues-doc-card">
      <div className="ues-doc-card__preview">
        {blobInfo?.loading ? (
          <div className="ues-doc-card__loading">
            <CircularProgress
              size={28}
              thickness={4}
              sx={{ color: "#4f46e5" }}
            />
          </div>
        ) : blobInfo?.error || !blobInfo?.url ? (
          <div className="ues-doc-card__no-preview">
            <span className="ues-doc-card__ext">{doc.fileType || "FILE"}</span>
            <span className="ues-doc-card__no-preview-text">
              Preview unavailable
            </span>
          </div>
        ) : isImage ? (
          <img
            className="ues-doc-card__img"
            src={blobInfo.url}
            alt={doc.fileName}
          />
        ) : isPdf ? (
          <div className="ues-doc-card__no-preview">
            <span className="ues-doc-card__ext">PDF</span>
            <span className="ues-doc-card__no-preview-text">
              {doc.fileName}
            </span>
          </div>
        ) : (
          <div className="ues-doc-card__no-preview">
            <span className="ues-doc-card__ext">{doc.fileType || "FILE"}</span>
            <span className="ues-doc-card__no-preview-text">
              {doc.fileName}
            </span>
          </div>
        )}

        {/* Fullscreen overlay button */}
        {blobInfo?.url && (
          <button
            className="ues-doc-card__fullscreen"
            onClick={openFullscreen}
            title="Open fullscreen"
          >
            <IconFullscreen />
          </button>
        )}
      </div>

      <div className="ues-doc-card__info">
        <p className="ues-doc-card__name" title={doc.fileName}>
          {doc.fileName}
        </p>
        <p className="ues-doc-card__meta">
          <span className="ues-doc-card__type-badge">
            {doc.fileType || "—"}
          </span>
          <span className="ues-doc-card__created">{doc.createdOn}</span>
        </p>
      </div>
    </div>
  );
};

// ── View Documents modal ──────────────────────────────────────────────────────
const ViewDocumentsModal = ({
  isOpen,
  onClose,
  row,
  docList,
  docListLoading,
  docBlobs,
}) => {
  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Uploaded Documents"
      size="large"
      entityID={row?.entityId}
    >
      <div className="ues-docs-modal">
        {/* Header info */}
        {row && (
          <div className="ues-docs-modal__header">
            <div className="ues-docs-modal__avatar">
              {(row.companyName || "?")[0].toUpperCase()}
            </div>
            <div>
              <p className="ues-docs-modal__company">
                {row.companyName || "—"}
              </p>
              <p className="ues-docs-modal__entity">{row.entityId}</p>
            </div>
            {docList.length > 0 && (
              <span className="ues-docs-modal__count">
                {docList.length} document{docList.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        )}

        <div className="ues-docs-modal__divider" />

        {/* Content */}
        {docListLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "12px",
              padding: "60px 0",
            }}
          >
            <CircularProgress
              size={40}
              thickness={4}
              sx={{ color: "#4f46e5" }}
              aria-label="Loading documents…"
            />
            <p className="ues-loading__text">Loading documents…</p>
          </Box>
        ) : docList.length === 0 ? (
          <div className="ues-docs-modal__empty">
            <span className="ues-docs-modal__empty-icon">📂</span>
            <p>No documents found for this entry.</p>
          </div>
        ) : (
          <div className="ues-docs-modal__grid">
            {docList.map((doc) => (
              <DocCard key={doc.id} doc={doc} blobInfo={docBlobs[doc.id]} />
            ))}
          </div>
        )}

        <div className="ues-docs-modal__footer">
          <button className="ues-btn ues-btn--cancel" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ── Main page ──────────────────────────────────────────────────────────────────
const UploadExcelSheet = () => {
  const {
    uploadModalOpen,
    uploadType,
    handleOpenModal,
    handleCloseModal,
    file,
    handleFileChange,
    uploading,
    uploadProgress,
    handleUpload,
    tableData,
    totalRecords,
    totalPages,
    currentPage,
    setCurrentPage,
    loading,
    searchQuery,
    handleSearchChange,
    handleResend,
    PAGE_SIZE,
    docModalOpen,
    docModalRow,
    docList,
    docListLoading,
    docBlobs,
    handleViewDocuments,
    handleCloseDocModal,
  } = useUploadExcelSheet();

  const [viewRow, setViewRow] = useState(null);

  return (
    <div className="ues-page">
      {/* Header */}
      <div className="ues-header">
        <div>
          <h1 className="ues-header__title">Upload Excel Sheet</h1>
          <p className="ues-header__sub">
            Upload an Excel file to import entity onboarding data
            {totalRecords > 0 && (
              <span className="ues-header__count">
                {" "}
                · {totalRecords} records
              </span>
            )}
          </p>
        </div>
        <div className="uploader-buttons">
          {/* Upload zip/folder of documents */}
          <button
            className="ues-btn ues-btn--folder"
            onClick={() => handleOpenModal("folder")}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
            Upload Folder
          </button>
          {/* Upload Excel sheet */}
          <button
            className="ues-btn ues-btn--upload"
            onClick={() => handleOpenModal("excel")}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="16 16 12 12 8 16" />
              <line x1="12" y1="12" x2="12" y2="21" />
              <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
            </svg>
            Upload Excel Sheet
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="ues-search-wrap">
        <div className="ues-search">
          <svg
            className="ues-search__icon"
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="ues-search__input"
            placeholder="Search by Entity ID or Company Name..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {searchQuery && (
            <button
              className="ues-search__clear"
              onClick={() => handleSearchChange("")}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="ues-table-card">
        <div className="ues-table-scroll">
          <table className="ues-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Entity ID</th>
                <th>Company Name</th>
                <th>Pan Number</th>
                <th>MSME</th>
                <th>Created On</th>
                <th className="th-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="ues-loading-row">
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "12px",
                        padding: "60px 0",
                      }}
                    >
                      <CircularProgress
                        size={40}
                        thickness={4}
                        sx={{ color: "#4f46e5" }}
                        aria-label="Loading…"
                      />
                      <p className="ues-loading__text">Loading…</p>
                    </Box>
                  </td>
                </tr>
              ) : tableData.length === 0 ? (
                <tr>
                  <td colSpan={7} className="ues-no-data">
                    {searchQuery
                      ? `No results for "${searchQuery}"`
                      : "No data available. Upload an Excel file to get started."}
                  </td>
                </tr>
              ) : (
                tableData.map((row, i) => (
                  <tr key={row.id || i}>
                    <td className="ues-td--num">
                      {(currentPage - 1) * PAGE_SIZE + i + 1}
                    </td>
                    <td>
                      <span className="col-entity-id">
                        {row.entityId || "—"}
                      </span>
                    </td>
                    <td>{row.companyName || "—"}</td>
                    <td>{row.pan || "—"}</td>
                    <td>{row.msme || "—"}</td>
                    <td>{row.createdOn || "—"}</td>
                    <td
                      className="td-actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <RowActionMenu
                        row={row}
                        onResend={handleResend}
                        onView={setViewRow}
                        onViewDocuments={handleViewDocuments}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && totalRecords > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalRecords}
            itemsPerPage={PAGE_SIZE}
            onPageChange={(p) => setCurrentPage(p)}
            showInfo={true}
          />
        )}
      </div>

      {/* View All Data Modal */}
      <ViewModal
        isOpen={!!viewRow}
        onClose={() => setViewRow(null)}
        row={viewRow}
      />

      {/* View Documents Modal */}
      <ViewDocumentsModal
        isOpen={docModalOpen}
        onClose={handleCloseDocModal}
        row={docModalRow}
        docList={docList}
        docListLoading={docListLoading}
        docBlobs={docBlobs}
      />

      {/* Upload Modal (Excel or Folder) */}
      <Modal
        isOpen={uploadModalOpen}
        onClose={handleCloseModal}
        title={
          uploadType === "folder" ? "Upload Folder / ZIP" : "Upload Excel Sheet"
        }
        size="small"
      >
        <div className="ues-modal-body">
          <p className="ues-modal-body__hint">
            {uploadType === "folder"
              ? "Select a ZIP file containing onboarding documents."
              : "Select an Excel file (.xlsx, .xls) to bulk import onboarding data."}
          </p>
          {/* File picker — hidden while uploading */}
          {!uploading && (
            <label className="ues-upload-zone" htmlFor="ues-file-input">
              <input
                id="ues-file-input"
                type="file"
                accept={
                  uploadType === "folder" ? ".zip,.rar,.7z" : ".xlsx,.xls"
                }
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              {file ? (
                <span className="ues-upload-zone__filename">
                  📎 {file.name}
                </span>
              ) : (
                <>
                  {uploadType === "folder" ? (
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                    </svg>
                  ) : (
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="16 16 12 12 8 16" />
                      <line x1="12" y1="12" x2="12" y2="21" />
                      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                    </svg>
                  )}
                  <span>
                    {uploadType === "folder"
                      ? "Click to choose ZIP file"
                      : "Click to choose Excel file"}
                  </span>
                  <span className="ues-upload-zone__hint">
                    {uploadType === "folder"
                      ? ".zip, .rar, .7z"
                      : ".xlsx or .xls"}
                  </span>
                </>
              )}
            </label>
          )}

          {/* Progress UI — shown while uploading */}
          {uploading && (
            <div className="ues-progress-wrap">
              {/* Circular ring with % in center */}
              <div className="ues-progress-circle-outer">
                <Box sx={{ position: "relative", display: "inline-flex" }}>
                  {/* Grey track ring */}
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={120}
                    thickness={4}
                    sx={{
                      color: "#e5e7eb",
                      position: "absolute",
                      top: 0,
                      left: 0,
                    }}
                  />
                  {/* Colored fill ring */}
                  <CircularProgress
                    variant="determinate"
                    value={uploadProgress}
                    size={120}
                    thickness={4}
                    sx={{
                      color: uploadProgress === 100 ? "#16a34a" : "#4f46e5",
                      transition: "color 0.4s",
                    }}
                  />
                  {/* Center text */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <span className="ues-progress-pct">{uploadProgress}%</span>
                    {uploadProgress === 100 && (
                      <span className="ues-progress-check">✓</span>
                    )}
                  </Box>
                </Box>
              </div>

              {/* Status label */}
              <p className="ues-progress-label">
                {uploadProgress === 100
                  ? "Upload complete!"
                  : uploadType === "folder"
                    ? "Uploading documents…"
                    : "Uploading Excel sheet…"}
              </p>

              {/* Linear bar */}
              <div className="ues-progress-bar-track">
                <div
                  className={`ues-progress-bar-fill${
                    uploadProgress === 100 ? " ues-progress-bar-fill--done" : ""
                  }`}
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              {/* <p className="ues-progress-bar-pct">{uploadProgress}% uploaded</p> */}

              {/* File name */}
              {file && <p className="ues-progress-filename">📎 {file.name}</p>}
            </div>
          )}

          <div className="ues-modal-footer">
            <button
              className="ues-btn ues-btn--cancel"
              onClick={handleCloseModal}
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              className="ues-btn ues-btn--submit"
              onClick={handleUpload}
              disabled={!file || uploading}
            >
              {uploading ? `${uploadProgress}%` : "Upload"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UploadExcelSheet;
